/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

import * as strings from '@touchplan/strings';
import { AutomationIdDirective } from '../../shared/directives/automation-id/automation-id.directive';
import { MatIconModule } from '@angular/material/icon';
import {
	IPromiseNowPosition,
	PromisePeriodService,
} from '../../modules/plan/services/promise-period.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GenericDialogComponent } from '../../common/generic-dialog/generic-dialog.component';

@Component({
	selector: 'tp-promise-now-button',
	standalone: true,
	imports: [CommonModule, MatButtonModule, AutomationIdDirective, MatIconModule],
	templateUrl: './promise-now-button.component.html',
	styleUrls: ['./promise-now-button.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromiseNowButtonComponent implements OnInit, OnDestroy {
	@HostBinding('style.top.px') public top = 0;
	@HostBinding('style.left.px') public left = 0;
	@HostBinding('style.display') public display = 'none';

	public promiseNowButtonText: string = strings.UICONTROL_TEXT_PROMISE_NOW;
	private dialogRef: MatDialogRef<GenericDialogComponent> | undefined;
	private shutdownSubject$ = new Subject<void>();

	constructor(private promisePeriodService: PromisePeriodService, private matDialog: MatDialog) {}

	public ngOnInit() {
		this.promisePeriodService.position$?.pipe(takeUntil(this.shutdownSubject$)).subscribe((position) => {
			this.updatePosition(position);
		});
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	/**
	 * Updates the position and display of the promise now button.
	 * @param position the IPromiseNowPosition to use to set the button position
	 */
	private updatePosition(position: IPromiseNowPosition) {
		if (position) {
			this.left = position.x;
			this.top = position.y;
			if (position.visible) {
				this.display = 'inline-block';
			} else {
				this.display = 'none';
			}
		} else {
			this.display = 'none';
		}
	}

	/**
	 * Called on click of the promise now button.
	 * Displays a confirmation dialog and calls the promisePeriodService to lock the current period if confirmed.
	 */
	public lockCurrentPeriod() {
		if (!this.dialogRef) {
			// only open dialog if it's not already open
			this.dialogRef = this.matDialog.open(GenericDialogComponent, {
				data: {
					title: strings.PROMISE_NOW_CONFIRMATION_TITLE,
					content: [strings.PROMISE_NOW_CONFIRMATION_SUB_TITLE, strings.PROMISE_NOW_CONFIRMATION_SUB_TITLE2],
					noButtonText: strings.PROMISE_NOW_CONFIRMATION_CANCEL,
					yesButtonText: strings.PROMISE_NOW_CONFIRMATION_PROMISE_NOW,
				},
				disableClose: true,
			});
			this.dialogRef.afterClosed().subscribe((confirmed: boolean) => {
				this.dialogRef = undefined;
				if (confirmed) {
					this.promisePeriodService.lockFromCurrent();
				}
			});
		}
	}
}
